import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Calendar,
  CaretRight,
  ChatText,
  Database,
  Envelope,
  FileText,
  GearSix,
  Globe,
  Key,
  Lightning,
  Microphone,
  Phone,
  Shield,
  Sparkle,
  WebhooksLogo
} from '@phosphor-icons/react';

/**
 * QuickActionsPanel - Contextual next steps based on agent configuration
 * 
 * Analyzes current agent config and suggests relevant actions:
 * - Missing capabilities
 * - Recommended integrations
 * - Configuration improvements
 * - Testing suggestions
 */
const QuickActionsPanel = ({ agentConfig, onAction, collapsed = false }) => {
  
  // Analyze config and generate contextual suggestions
  const suggestions = useMemo(() => {
    const actions = [];
    const { 
      capability_profile, 
      accepted_inputs = [], 
      required_capabilities = [],
      capability_bindings = {},
      name,
      description,
      system_prompt
    } = agentConfig;

    // === Identity Suggestions ===
    if (!name?.trim()) {
      actions.push({
        id: 'add_name',
        category: 'identity',
        priority: 'high',
        icon: GearSix,
        title: 'Add Agent Name',
        description: 'Give your agent a descriptive name',
        action: 'focus_name'
      });
    }

    if (!description?.trim()) {
      actions.push({
        id: 'add_description',
        category: 'identity',
        priority: 'medium',
        icon: FileText,
        title: 'Add Description',
        description: 'Help users understand what this agent does',
        action: 'focus_description'
      });
    }

    // === Voice/Booking Suggestions ===
    if (capability_profile === 'live_voice_s2s_agent' || 
        required_capabilities.some(c => c.includes('voice') || c.includes('booking'))) {
      
      if (!capability_bindings?.calendar_read) {
        actions.push({
          id: 'add_calendar',
          category: 'integration',
          priority: 'high',
          icon: Calendar,
          title: 'Add Calendar Integration',
          description: 'Connect Google Calendar or Microsoft 365',
          action: 'add_capability',
          capability: 'calendar_read'
        });
      }

      if (!capability_bindings?.booking_create) {
        actions.push({
          id: 'add_booking',
          category: 'integration',
          priority: 'high',
          icon: Calendar,
          title: 'Configure Booking Provider',
          description: 'Set up Square, Calendly, or custom booking',
          action: 'add_capability',
          capability: 'booking_create'
        });
      }

      if (!capability_bindings?.sms_send) {
        actions.push({
          id: 'add_sms',
          category: 'integration',
          priority: 'medium',
          icon: ChatText,
          title: 'Enable SMS Confirmations',
          description: 'Send booking confirmations via SMS',
          action: 'add_capability',
          capability: 'sms_send'
        });
      }
    }

    // === WebhooksLogo/Integration Suggestions ===
    if (accepted_inputs.includes('webhook') || accepted_inputs.includes('api_payload')) {
      if (!capability_bindings?.webhook_receive) {
        actions.push({
          id: 'configure_webhook',
          category: 'integration',
          priority: 'high',
          icon: WebhooksLogo,
          title: 'Configure Webhook Endpoint',
          description: 'Set up incoming webhook handling',
          action: 'add_capability',
          capability: 'webhook_receive'
        });
      }
    }

    // === Document Processing Suggestions ===
    if (accepted_inputs.some(i => i.includes('file_') || i.includes('pdf') || i.includes('docx'))) {
      if (!required_capabilities.includes('document_parse')) {
        actions.push({
          id: 'add_doc_parse',
          category: 'capability',
          priority: 'medium',
          icon: FileText,
          title: 'Add Document Parsing',
          description: 'Extract text and data from documents',
          action: 'add_capability',
          capability: 'document_parse'
        });
      }
    }

    // === Email Suggestions ===
    if (required_capabilities.some(c => c.includes('email'))) {
      if (!capability_bindings?.email_send) {
        actions.push({
          id: 'configure_email',
          category: 'integration',
          priority: 'medium',
          icon: Envelope,
          title: 'Configure Email Provider',
          description: 'Set up SendGrid or SMTP for emails',
          action: 'add_capability',
          capability: 'email_send'
        });
      }
    }

    // === MCP Integration Suggestions ===
    if (required_capabilities.some(c => c.includes('automation') || c.includes('workflow'))) {
      actions.push({
        id: 'add_mcp',
        category: 'integration',
        priority: 'medium',
        icon: Lightning,
        title: 'Connect Automation Platform',
        description: 'Link with Zapier, Make, or n8n via MCP',
        action: 'open_mcp_settings'
      });
    }

    // === Security Suggestions ===
    if (!system_prompt?.includes('security') && !system_prompt?.includes('sensitive')) {
      actions.push({
        id: 'review_security',
        category: 'security',
        priority: 'low',
        icon: Shield,
        title: 'Review Security Settings',
        description: 'Ensure proper data handling guidelines',
        action: 'open_security_config'
      });
    }

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return actions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }, [agentConfig]);

  // Group suggestions by category
  const groupedSuggestions = useMemo(() => {
    const groups = {};
    suggestions.forEach(s => {
      if (!groups[s.category]) groups[s.category] = [];
      groups[s.category].push(s);
    });
    return groups;
  }, [suggestions]);

  const categoryLabels = {
    identity: 'Setup',
    integration: 'Integrations',
    capability: 'Capabilities',
    security: 'Security',
    testing: 'Testing'
  };

  const categoryColors = {
    identity: 'text-blue-400',
    integration: 'text-purple-400',
    capability: 'text-emerald-400',
    security: 'text-amber-400',
    testing: 'text-cyan-400'
  };

  if (collapsed || suggestions.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-white/10 bg-[#0a0b0f]">
      {/* Header */}
      <div className="p-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Lightning className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-white">Quick Actions</span>
          <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">
            {suggestions.length}
          </span>
        </div>
      </div>

      {/* Actions List */}
      <div className="max-h-[200px] overflow-y-auto p-2 space-y-1">
        <AnimatePresence>
          {suggestions.slice(0, 5).map((action, index) => (
            <motion.button
              key={action.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => onAction(action)}
              className={`
                w-full flex items-center gap-3 p-2 rounded-lg text-left
                bg-white/[0.02] hover:bg-white/5 border border-transparent
                hover:border-white/10 transition-all group
                ${action.priority === 'high' ? 'border-l-2 border-l-amber-500/50' : ''}
              `}
              data-testid={`quick-action-${action.id}`}
            >
              <div className={`p-1.5 rounded-md bg-white/5 ${categoryColors[action.category]}`}>
                <action.icon className="w-3.5 h-3.5" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">
                  {action.title}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {action.description}
                </div>
              </div>

              <CaretRight className="w-4 h-4 text-slate-600 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
            </motion.button>
          ))}
        </AnimatePresence>

        {suggestions.length > 5 && (
          <div className="text-xs text-slate-500 text-center py-1">
            +{suggestions.length - 5} more suggestions
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickActionsPanel;
